// Floating status toast rendered above the workspace content. Extracted
// from `LeadWorkspaceShell.tsx` to keep the shell under the file-size
// hygiene soft cap; no behavior change.
//
// v1.0 Lead Control polish (slice B3): the optional `action` field lets
// the toast carry a clickable deep-link — used today by the
// "Lead sent to GHL" success branch to surface the resolved CRM URL so
// the rep can jump straight into the org/location's CRM. Length and
// auto-dismiss tuning of action toasts lives in
// `useLeadWorkspaceRuntimeBasics`.

import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

export type WorkspaceToastAction = {
  /** Short verb-style label rendered inside the link, e.g. "Open in CRM". */
  label: string;
  /** Fully-qualified URL. Opens in a new tab; no fallback rendered when null. */
  url: string;
};

export type WorkspaceToast = {
  message: string;
  type: "success" | "error";
  /** Optional click-through affordance shown after the message. */
  action?: WorkspaceToastAction;
};

export type WorkspaceToastOverlayProps = {
  toast: WorkspaceToast | null;
};

export function WorkspaceToastOverlay({ toast }: WorkspaceToastOverlayProps) {
  if (!toast) return null;
  const fireflyTarget = toast.action ? { [FIREFLY_TARGET_ATTR]: "crm-send-toast" } : {};
  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      <div
        {...fireflyTarget}
        className={
          "flex items-center gap-3 px-4 py-3 rounded-2xl shadow-lg text-sm sm:text-base " +
          (toast.type === "success" ? "bg-emerald-600 text-white" : "bg-amber-600 text-white")
        }
      >
        <span>{toast.message}</span>
        {toast.action ? (
          <a
            href={toast.action.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white underline-offset-2 hover:bg-white/25 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            {toast.action.label}
          </a>
        ) : null}
      </div>
    </div>
  );
}
